import { isAccepted, normalizeWord, pickPuzzle, } from './data';
import { timing, variantForLevel, } from './params';
export function makeStimulus(trialIdx, letterCount, level, lang) {
    const variant = variantForLevel(level);
    // Pour bilingue, on alterne lang sur trialIdx pair/impair.
    const effectiveLang = variant.bilingual
        ? trialIdx % 2 === 0
            ? lang
            : lang === 'fr'
                ? 'en'
                : 'fr'
        : lang;
    const clampedCount = Math.max(4, Math.min(9, Math.round(letterCount)));
    const puzzle = pickPuzzle(effectiveLang, clampedCount, trialIdx + 1);
    const targetScore = clampedCount * variant.targetMultiplier;
    return {
        trialIdx,
        durationMs: variant.roundMs,
        letterCount: puzzle.letters.length,
        puzzle,
        lang: effectiveLang,
        targetScore,
    };
}
export function createWordPyramidsAdapter(level = 1, lang = 'fr') {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        acceptedWords: [],
        rejectedWords: [],
        remainingMs: 0,
    };
    void lang;
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    let tickInterval = null;
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(stim) {
        if (!resolver)
            return;
        const score = state.acceptedWords.reduce((a, w) => a + w.length, 0);
        const r = resolver;
        resolver = null;
        rejecter = null;
        clearTimers();
        r({
            acceptedWords: [...state.acceptedWords],
            score,
            reactionTimeMs: Date.now() - trialStart,
            correct: score >= stim.targetScore,
        });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    acceptedWords: [],
                    rejectedWords: [],
                    remainingMs: stim.durationMs,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'compose' });
                    trialStart = Date.now();
                    // Tick toutes les 250ms pour MAJ remainingMs (UI timer).
                    tickInterval = setInterval(() => {
                        const elapsed = Date.now() - trialStart;
                        const remaining = Math.max(0, stim.durationMs - elapsed);
                        if (remaining <= 0) {
                            if (tickInterval !== null) {
                                clearInterval(tickInterval);
                                tickInterval = null;
                            }
                            if (state.subPhase === 'compose') {
                                update({ remainingMs: 0 });
                                settle(stim);
                            }
                        }
                        else {
                            update({ remainingMs: remaining });
                        }
                    }, 250);
                    // Hard cap : timeout absolu si setInterval rate.
                    timers.push(setTimeout(() => {
                        if (state.subPhase === 'compose') {
                            settle(stim);
                        }
                    }, stim.durationMs + 100));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        submitWord(input) {
            if (state.subPhase !== 'compose' || state.stim === null) {
                return { accepted: false, reason: 'phase' };
            }
            const word = normalizeWord(input);
            if (word.length < 3) {
                return { accepted: false, reason: 'too-short' };
            }
            if (state.acceptedWords.includes(word)) {
                return { accepted: false, reason: 'duplicate' };
            }
            const puzzle = state.stim.puzzle;
            if (!isAccepted(word, puzzle)) {
                update({ rejectedWords: [...state.rejectedWords, word] });
                return { accepted: false, reason: 'not-in-dict' };
            }
            update({ acceptedWords: [...state.acceptedWords, word] });
            return { accepted: true };
        },
        finishRound() {
            if (state.subPhase !== 'compose' || state.stim === null)
                return;
            settle(state.stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map